/**
 * @file useSystemMetrics.ts
 * @description Real-time Firestore listener for the system_metrics "latest" document.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { SystemMetrics } from "../types";
import { MOCK_SYSTEM_METRICS } from "../data/mockData";

const MOCK_MODE = import.meta.env.VITE_USE_EMULATORS !== "true" &&
  !import.meta.env.VITE_FIREBASE_PROJECT_ID?.includes("your-project");

interface UseSystemMetricsResult {
  metrics: SystemMetrics | null;
  loading: boolean;
  error: string | null;
}

export function useSystemMetrics(): UseSystemMetricsResult {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (MOCK_MODE) {
      setMetrics(MOCK_SYSTEM_METRICS);
      setLoading(false);
      return;
    }

    const docRef = doc(db, "system_metrics", "latest");

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setMetrics(snapshot.data() as SystemMetrics);
        } else {
          setMetrics(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("[useSystemMetrics] Firestore error:", err);
        setError(`Failed to load system metrics: ${err.message}`);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { metrics, loading, error };
}
